import {
  useMutationMembershipSignupCustomPayment,
  useMutationMembershipSignupPaymentMethod,
  useMutationProductOrderCheckoutCustomPayment,
  useMutationProductOrderCheckoutPaymentMethod,
  useMutationSessionPackSignupCustomPayment,
  useMutationSessionPackSignupPaymentMethod,
} from "@gymflow/api";
import { CustomPaymentCategoryDTO, InvoiceStatus } from "@gymflow/types";
import { useMutation } from "@tanstack/react-query";
import useGymflowModels from "apps/portal/src/store";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { useCallback } from "react";

import { OptionType } from "../../../atoms/base/components/OptionsList/components/OptionItem";
import { ShopCartType } from "../../ShopTypes";
import { TabsType } from "../ShopSidebarProvider";

type ProcessCheckoutProps = {
  cart: ShopCartType;
  activeTabId: TabsType;
  orderKey: string;
  values: {
    priceOverride?: number;
    inPersonPaymentMethod?: OptionType<CustomPaymentCategoryDTO>;
    paymentMethodId?: string;
  };
  startDate: string;
  userMemberId: string;
  promotionCode?: string;
  paymentMethodId?: string;
  priceOverride?: number;
  customPaymentsEnabled: boolean;
  walletAmount?: number;
};

export const useProcessCheckout = () => {
  const { api } = useGymflowModels();

  const { mutateAsync: membershipSignupCustomPaymentMutateAsync } =
    useMutationMembershipSignupCustomPayment({ api });
  const { mutateAsync: membershipSignupPaymentMethodMutateAsync } =
    useMutationMembershipSignupPaymentMethod({ api });
  const { mutateAsync: productOrderCheckoutCustomPaymentMutateAsync } =
    useMutationProductOrderCheckoutCustomPayment({ api });
  const { mutateAsync: productOrderCheckoutPaymentMethodMutateAsync } =
    useMutationProductOrderCheckoutPaymentMethod({ api });
  const { mutateAsync: sessionPackSignupCustomPaymentMutateAsync } =
    useMutationSessionPackSignupCustomPayment({ api });
  const { mutateAsync: sessionPackSignupPaymentMethodMutateAsync } =
    useMutationSessionPackSignupPaymentMethod({ api });
  const { mutateAsync: orderVoucher } = useMutation(
    apiV2.staff.voucher.mutations.order(),
  );
  const { mutateAsync: customPayment } = useMutation(
    apiV2.staff.voucher.mutations.customPayment(),
  );

  const processCheckout = useCallback(
    async ({
      cart,
      activeTabId,
      orderKey,
      values,
      startDate,
      userMemberId,
      promotionCode,
      paymentMethodId,
      priceOverride,
      customPaymentsEnabled,
      walletAmount,
    }: ProcessCheckoutProps) => {
      let result: { invoiceNumber?: string; invoiceStatus?: InvoiceStatus } =
        {};

      if (cart.type === "MEMBERSHIP") {
        if (customPaymentsEnabled) {
          if (activeTabId === "IN_PERSON") {
            result = await membershipSignupCustomPaymentMutateAsync({
              membershipId: cart.payload.id,
              startDate,
              orderKey,
              customPaymentCategoryId:
                (values?.inPersonPaymentMethod?.id as number) || 0,
              priceOverride,
              promotionCode,
              userMemberId,
              walletAmount,
            });
          } else if (activeTabId === "ONLINE") {
            result = await membershipSignupPaymentMethodMutateAsync({
              membershipId: cart.payload.id,
              startDate,
              orderKey,
              paymentMethodId,
              priceOverride,
              promotionCode,
              userMemberId,
              walletAmount,
            });
          }
        } else {
          result = await membershipSignupPaymentMethodMutateAsync({
            membershipId: cart.payload.id,
            startDate,
            orderKey,
            paymentMethodId,
            priceOverride,
            promotionCode,
            userMemberId,
            walletAmount,
          });
        }
      } else if (cart.type === "CREDIT_PACK") {
        if (customPaymentsEnabled) {
          if (activeTabId === "IN_PERSON") {
            result = await sessionPackSignupCustomPaymentMutateAsync({
              sessionPackId: cart.payload.id,
              startDate,
              orderKey,
              customPaymentCategoryId:
                (values?.inPersonPaymentMethod?.id as number) || 0,
              priceOverride,
              promotionCode,
              userMemberId,
              walletAmount,
            });
          } else if (activeTabId === "ONLINE") {
            result = await sessionPackSignupPaymentMethodMutateAsync({
              sessionPackId: cart.payload.id,
              startDate,
              orderKey,
              paymentMethodId,
              priceOverride,
              promotionCode,
              userMemberId,
              walletAmount,
            });
          }
        } else {
          result = await sessionPackSignupPaymentMethodMutateAsync({
            sessionPackId: cart.payload.id,
            startDate,
            orderKey,
            paymentMethodId,
            priceOverride,
            promotionCode,
            userMemberId,
            walletAmount,
          });
        }
      } else if (cart.type === "PRODUCT") {
        const products = cart.payload.map((e) => ({
          productId: e.product.id,
          quantity: e.quantity,
        }));
        if (customPaymentsEnabled) {
          if (activeTabId === "IN_PERSON") {
            result = await productOrderCheckoutCustomPaymentMutateAsync({
              orderItems: products,
              orderKey,
              customPaymentCategoryId:
                (values?.inPersonPaymentMethod?.id as number) || 0,
              priceOverride,
              promotionCode,
              userMemberId,
              walletAmount,
            });
          } else if (activeTabId === "ONLINE") {
            result = await productOrderCheckoutPaymentMethodMutateAsync({
              orderItems: products,
              orderKey,
              paymentMethodId,
              priceOverride,
              promotionCode,
              userMemberId,
              walletAmount,
            });
          }
        } else {
          result = await productOrderCheckoutPaymentMethodMutateAsync({
            orderItems: products,
            orderKey,
            paymentMethodId,
            priceOverride,
            promotionCode,
            userMemberId,
            walletAmount,
          });
        }
      } else if (cart.type === "GIFT_VOUCHER") {
        if (activeTabId === "IN_PERSON") {
          result = await customPayment({
            orderKey,
            customPaymentCategoryId:
              (values?.inPersonPaymentMethod?.id as number) || 0,
            amount: values.priceOverride,
            userMemberId,
          });
        } else {
          result = await orderVoucher({
            orderKey,
            paymentMethodId: paymentMethodId ?? "",
            amount: values.priceOverride,
            userMemberId,
          });
        }
      }
      return result;
    },
    [
      membershipSignupCustomPaymentMutateAsync,
      membershipSignupPaymentMethodMutateAsync,
      sessionPackSignupCustomPaymentMutateAsync,
      sessionPackSignupPaymentMethodMutateAsync,
      productOrderCheckoutCustomPaymentMutateAsync,
      productOrderCheckoutPaymentMethodMutateAsync,
      customPayment,
      orderVoucher,
    ],
  );

  return { processCheckout };
};
